import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "../../../../components/ui/Button";
import { useMyImpressions } from "../../../../queries/adMarketplace";

const AD_TYPE_ICON = {
    video:          "pi pi-video",
    image:          "pi pi-image",
    ticker:         "pi pi-align-left",
    silent_visual:  "pi pi-volume-off",   // full-screen, audio untouched
};

export default function ImpressionsPanel() {
    const { t }               = useTranslation();
    const [page, setPage]     = useState(1);
    const { data, isLoading } = useMyImpressions(page);

    const impressions = data?.data ?? [];
    const meta        = data?.meta ?? {};

    return (
        <div>
            <h4 className="font-semibold text-gray-700 mb-3 text-sm">{t("ad_marketplace.impressions_title")}</h4>

            {isLoading ? (
                <div className="flex justify-center py-10"><ProgressSpinner /></div>
            ) : impressions.length === 0 ? (
                <p className="text-center text-gray-400 py-10 text-sm">{t("ad_marketplace.no_impressions")}</p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b text-xs text-gray-500 uppercase tracking-wide">
                                    <th className="pb-2 text-left">{t("ad_marketplace.col_date_time")}</th>
                                    <th className="pb-2 text-left">{t("ad_marketplace.col_campaign")}</th>
                                    <th className="pb-2 text-left">{t("table.type")}</th>
                                    <th className="pb-2 text-left">{t("ad_marketplace.col_device")}</th>
                                    <th className="pb-2 text-right">{t("ad_marketplace.col_duration")}</th>
                                    <th className="pb-2 text-right">{t("ad_marketplace.col_points")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {impressions.map((imp) => (
                                    <tr key={imp.id} className="border-b last:border-0 hover:bg-gray-50">
                                        <td className="py-2 text-xs text-gray-500">
                                            {new Date(imp.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                                        </td>
                                        <td className="py-2 text-sm text-gray-700 max-w-40 truncate">
                                            {imp.campaign?.name ?? "—"}
                                        </td>
                                        <td className="py-2">
                                            <i className={`${AD_TYPE_ICON[imp.campaign?.ad_type] ?? "pi pi-circle"} text-blue-500`} />
                                            <span className="text-xs text-gray-500 ml-1 capitalize">{imp.campaign?.ad_type}</span>
                                        </td>
                                        <td className="py-2 text-xs text-gray-600">
                                            {imp.device?.name ?? imp.device?.serial_name ?? "—"}
                                        </td>
                                        <td className="py-2 text-right text-xs text-gray-500">
                                            {imp.duration_seconds}s
                                        </td>
                                        <td className="py-2 text-right font-semibold text-green-600">
                                            +{imp.points_awarded}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {meta.last_page > 1 && (
                        <div className="flex justify-center gap-2 mt-4">
                            <Button icon="pi pi-chevron-left" className="p-button-outlined p-button-sm"
                                disabled={page === 1} onClick={() => setPage(p => p - 1)} />
                            <span className="text-xs self-center text-gray-500">
                                {t("ad_marketplace.page_of", { current: meta.current_page, total: meta.last_page })}
                            </span>
                            <Button icon="pi pi-chevron-right" className="p-button-outlined p-button-sm"
                                disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
